import UserMenu from "~/components/UserMenu";
import { useAuth } from "~/hooks/useAuth";
import { useNavigate } from "react-router";
import UserMenuContent from "./UserMenuContent";
import { useDialogActions } from "~/store/dialogStore";
import { Bell, MailWarning } from "lucide-react";
import { NotificationsDialog } from "~/containers/account/configuration/NotificationsDialog";
import { Badge, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "snap-design-system";
import { useUserCRUD } from "~/hooks/useUserCRUD";
import { CHECK_SESSION_INTERVAL, USER_CONSTANTS } from "~/helpers/constants";
import { usePermissionCheck } from "./router/PermissionGuard";
import { Permission } from "~/helpers/permissions.helper";
import { useSessionGuard } from "~/hooks/useSessionGuard";
import { useNavigationGuard } from "~/hooks/useNavigationGuard";
import { useUnsavedChangesWarning } from "~/hooks/useUnsavedChangesWarning";
import { usePermissionManager } from "~/services/permissionManager";

type MenuItem = {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  testId?: string;
};

export default function UserNavbar() {
  const { openDialog } = useDialogActions();
  const navigate = useNavigate();
  const {
    logoutMutation: { mutateAsync: logout },
  } = useAuth();
  const { userInviteQuery, passwordResetRequestsQuery } = useUserCRUD();
  const enviadoStatus = USER_CONSTANTS.status_invite.enviado;
  const { data: invitesData } = userInviteQuery(enviadoStatus);
  const inviteCount = invitesData?.length || 0;
  const { checkPermission } = usePermissionCheck();
  const canViewInvites = checkPermission(Permission.GET_USER_INVITE);
  const { isAdmin } = usePermissionManager();
  const { data: resetRequests } = passwordResetRequestsQuery(isAdmin());
  const resetCount = resetRequests?.length || 0;
  const totalNotifications = inviteCount + resetCount;
  const { checkSessionBeforeAction } = useSessionGuard({ interval: CHECK_SESSION_INTERVAL[1] });
  const canViewSupport = checkPermission(Permission.VIEW_SUPPORT);
  const { shouldBlockNavigation, clearPendingChanges } = useNavigationGuard();
  const { showUnsavedChangesDialog } = useUnsavedChangesWarning({
    hasUnsavedChanges: shouldBlockNavigation,
    onConfirmNavigation: clearPendingChanges,
  });

  const handleLogout = () => {
    if (shouldBlockNavigation) {
      showUnsavedChangesDialog(() => {
        logout();
      });
      return;
    }
    logout();
  };

  const handleNavigateConfig = () => {
    if (shouldBlockNavigation) {
      showUnsavedChangesDialog(() => {
        navigate('/conta/configuracoes');
      });
      return;
    }
    navigate('/conta/configuracoes');
  };

  const handleNavigateSupport = async () => {
    if (shouldBlockNavigation) {
      showUnsavedChangesDialog(async () => {
        const isSessionValid = await checkSessionBeforeAction();
        if (isSessionValid) {
          navigate('/conta/suporte');
        }
      });
      return;
    }

    const isSessionValid = await checkSessionBeforeAction();
    if (isSessionValid) {
      navigate('/conta/suporte');
    }
  };

  const handleOpenNotificationsDialog = () => {
    openDialog({
      title: "Notificações",
      icon: <Bell />,
      content: <NotificationsDialog.Content />,
      className: "max-w-4xl",
    });
  };

  const notificationAction = {
    label: "NOTIFICAÇÕES",
    icon: <div className="h-3 w-3 bg-border" />,
    onClick: handleOpenNotificationsDialog,
  };
  const configurationAction = {
    label: "CONFIGURAÇÕES",
    icon: <div className="h-3 w-3 bg-border" />,
    onClick: handleNavigateConfig,
    testId: "snap-admin-settings-option",
  };
  const logoutAction = {
    label: "SAIR",
    icon: <div className="h-3 w-3 bg-border" />,
    onClick: handleLogout,
  };
  const supportAction = {
    label: "SUPORTE",
    icon: <div className="h-3 w-3 bg-border" />,
    onClick: handleNavigateSupport,
  };

  const menuActions: MenuItem[] = [
    configurationAction,
    ...(canViewInvites || isAdmin() ? [notificationAction] : []),
    ...(canViewSupport ? [supportAction] : []),
    logoutAction,
  ]

  const NotificationBadge = () => {
    if (totalNotifications === 0) return null;

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge
              variant={"accent"}
              className="absolute -top-4 -left-2 rounded-full! cursor-pointer"
              onClick={handleOpenNotificationsDialog}
            >
              <MailWarning size={12} />
              {totalNotifications}
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            <p>
              {inviteCount > 0 && `${inviteCount} convite${inviteCount > 1 ? "s" : ""}`}
              {inviteCount > 0 && resetCount > 0 && " · "}
              {resetCount > 0 && `${resetCount} reset${resetCount > 1 ? "s" : ""} de senha`}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  return (
    <div className="relative">
      <UserMenu userMenuContent={<UserMenuContent menuActions={menuActions} />} />
      {(canViewInvites || isAdmin()) && <NotificationBadge />}
    </div>
  );
}
